// src/components/communication/BroadcastForm.jsx
import React, { useState, useEffect } from 'react';
import Button from '../common/Button';
import Checkbox from '../common/Checkbox';

const BroadcastForm = ({ onSend, onCancel, threads }) => {
  const [message, setMessage] = useState('');
  const [selectAll, setSelectAll] = useState(true);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [customers, setCustomers] = useState([]);
  
  // Extract unique customers from threads
  useEffect(() => {
    const uniqueCustomers = [];
    const customerIds = new Set();
    
    threads.forEach(thread => {
      if (thread.customerId && !customerIds.has(thread.customerId)) {
        customerIds.add(thread.customerId);
        uniqueCustomers.push({
          id: thread.customerId,
          name: thread.customerName || 'Unknown Customer',
          email: thread.customerEmail || '',
        });
      }
    });
    
    setCustomers(uniqueCustomers);
  }, [threads]);
  
  // Handle select all change
  const handleSelectAllChange = (e) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    
    if (checked) {
      setSelectedCustomers([]);
    } else {
      setSelectedCustomers(customers.map(customer => customer.id));
    }
  };
  
  // Handle customer selection change
  const handleCustomerChange = (customerId, checked) => {
    if (checked) {
      setSelectedCustomers(prev => [...prev, customerId]);
    } else {
      setSelectedCustomers(prev => prev.filter(id => id !== customerId));
    }
    
    // Update selectAll state
    if (checked && selectedCustomers.length + 1 === customers.length) {
      setSelectAll(true);
    } else if (!checked && selectAll) {
      setSelectAll(false);
    }
  };
  
  // Handle send message
  const handleSend = () => {
    if (!message.trim()) return;
    
    onSend(message, selectAll ? [] : selectedCustomers);
  };
  
  return (
    <div>
      <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
        <div className="sm:flex sm:items-start">
          <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Send Broadcast Message
            </h3>
            <div className="mt-2">
              <p className="text-sm text-gray-500">
                Send a message to multiple customers at once.
              </p>
              
              <div className="mt-4">
                <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                  Message
                </label>
                <textarea
                  id="message"
                  rows={4}
                  className="mt-1 block w-full shadow-sm focus:ring-orange-500 focus:border-orange-500 sm:text-sm border border-gray-300 rounded-md"
                  placeholder="Enter your message..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </div>
              
              <div className="mt-4">
                <div className="flex items-center">
                  <Checkbox
                    id="select-all"
                    name="select-all"
                    checked={selectAll}
                    onChange={handleSelectAllChange}
                    label="Send to all customers"
                  />
                </div>
                
                {!selectAll && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select customers
                    </label>
                    <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-md p-2">
                      {customers.map(customer => (
                        <div key={customer.id} className="py-1">
                          <Checkbox
                            id={`customer-${customer.id}`}
                            name={`customer-${customer.id}`}
                            checked={selectedCustomers.includes(customer.id)}
                            onChange={(e) => handleCustomerChange(customer.id, e.target.checked)}
                            label={
                              <div>
                                <span className="font-medium">{customer.name}</span>
                                {customer.email && (
                                  <span className="text-xs text-gray-500 ml-2">({customer.email})</span>
                                )}
                              </div>
                            }
                          />
                        </div>
                      ))}
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Selected: {selectedCustomers.length} of {customers.length} customers
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
        <Button
          type="button"
          variant="primary"
          onClick={handleSend}
          disabled={!message.trim() || (!selectAll && selectedCustomers.length === 0)}
        >
          Send Broadcast
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          className="mt-3 sm:mt-0 sm:mr-3"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default BroadcastForm;